import React, { useState, useEffect } from 'react';
import PartySecret from './PartySecret';
import styled, { keyframes } from 'styled-components';
import image from "../Assets/party.jpg";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const spin = keyframes` to { transform: rotate(360deg); } `;

const Page = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #0f1720 0%, #113F42 100%);
  color: #fff;
  padding: 48px 24px;
  box-sizing: border-box;
`;

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Card = styled.section`
  position: relative;
  z-index: 1;
  text-align: left;
  width: 100%;
  padding: 48px;
  border-radius: 12px;
  background: rgba(255,255,255,0.03);
  box-shadow: 0 8px 24px rgba(2,6,23,0.6);

  @media (min-width: 768px) {
    padding-top: 140px;
    text-align: center;
  }
`;

const Headline = styled.h1`
  font-family: 'Prata', serif;
  font-size: 2.25rem;
  margin: 0 0 12px;
  color: #FFF2DF;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  max-width: 420px;
  margin: 8px auto 20px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.12);
  background: rgba(255,255,255,0.02);
  color: #fff;
  font-size: 1rem;
  box-sizing: border-box;

  &::placeholder { color: rgba(255,255,255,0.5); }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 4px rgba(255,242,223,0.08);
    border-color: #FFF2DF;
  }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(0,0,0,0.08);
  border-top-color: #0b0b0b;
  animation: ${spin} 0.8s linear infinite;
  margin-right: 10px;
  vertical-align: middle;
`;

const CTA = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #FFF2DF;
  color: #0b0b0b;
  padding: 14px 28px;
  border-radius: 999px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  text-decoration: none;
  transition: transform 120ms ease, background 120ms ease;
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);

  &:hover, &:focus {
    transform: translateY(-3px);
    background: #f1e6c5;
    outline: none;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Status = styled.p`
  margin-top: 16px;
  color: #b6c6c5;
  font-size: 0.95rem;
`;

/* Secret (inline) view styles */
const SecretContainer = styled.div`
  margin-top: 12px;
  text-align: center;
  color: #FFF2DF;
`;
const SecretCard = styled.div`
  margin-top: 16px;
  padding: 18px;
  border-radius: 10px;
  background: rgba(255,255,255,0.02);
  color: #dfe7e6;
  box-shadow: 0 6px 18px rgba(2,6,23,0.4);
  text-align: left;
`;
const SecretTitle = styled.h2`
  margin: 0 0 8px;
  color: #FFF2DF;
`;
const SecretLine = styled.p`
  margin: 6px 0;
  font-size: 1rem;
`;

const Image = styled.div`
  background-image: url(${image});
  height: 160px;
  width: 200px;
  background-size: cover;
  background-position: center;
  margin: 0 0 20px;

  @media (min-width: 768px) {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, -50%);
    margin: 0;
    height: 220px;
    width: 320px;
    box-shadow: 0 8px 24px rgba(2,6,23,0.6);
    z-index: 2;
  }
`;

const ACCESS_COOKIE_NAME = 'party_access';
const ACCESS_MAX_AGE = 14 * 24 * 60 * 60; // 14 days in seconds

function setAccessCookie() {
  document.cookie = `${ACCESS_COOKIE_NAME}=1; max-age=${ACCESS_MAX_AGE}; path=/; SameSite=Lax`;
}
function hasAccessCookie() {
  return document.cookie.split('; ').some(c => c.startsWith(`${ACCESS_COOKIE_NAME}=`));
}

export default function Party() {
  const [password, setPassword] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    if (hasAccessCookie()) {
      setAuthorized(true);
      setStatusMsg('Welcome back — access remembered for 14 days');
      console.log('Access cookie found — user remembered');
    }
  }, []);

  const handleCheck = async (e) => {
    e.preventDefault();
    const entered = (password || '').trim().toLowerCase();
    if (!entered) {
      setStatusMsg('Please enter a passcode');
      return;
    }

    setIsChecking(true);
    setStatusMsg('');
    try {
      const docRef = doc(db, 'secrets', 'birthday-party');
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.warn('Secret document not found');
        setStatusMsg('Secret not found');
        console.warn('FAIL');
      } else {
        const secretRaw = docSnap.data().passcode;
        const secret = (secretRaw ?? '').toString().trim().toLowerCase();
        if (secret === entered) {
          console.log('PASS');
          setStatusMsg('Access granted. You will not be asked again for 14 days.');
          setAccessCookie();
          setAuthorized(true);
        } else {
          console.log('FAIL');
          setStatusMsg('Passcode incorrect');
        }
      }
    } catch (err) {
      console.error('Error checking passcode:', err);
      setStatusMsg('Error checking passcode');
    } finally {
      setIsChecking(false);
    }
  };

  const clearAccess = () => {
    document.cookie = `${ACCESS_COOKIE_NAME}=; max-age=0; path=/; SameSite=Lax`;
    setAuthorized(false);
    setStatusMsg('Access cleared');
    setPassword('');
  };

  return (
    <div>

          {authorized ? (
                <PartySecret onLogout={clearAccess} setStatusMsg={setStatusMsg} />
           ) : (
                <Page>
                <CardWrapper>
                    <Image />
                    <Card aria-labelledby="party-title">
                <Headline id="party-title">
            {authorized ? 'Welcome!' : 'Enter Passcode'}
            </Headline>

            <form onSubmit={handleCheck}>
              <label htmlFor="party-input" style={{ position: 'absolute', left: '-9999px' }}>
                Passcode
              </label>

              <Input
                id="party-input"
                type="text"
                placeholder="Type passcode here"
                value={password}
                onChange={(e) => setPassword(e.target.value.toLowerCase())}
                aria-label="Passcode"
                autoComplete="off"
                disabled={isChecking}
              />

              <CTA
                type="submit"
                disabled={isChecking}
                aria-label="Check passcode"
                aria-busy={isChecking}
              >
                {isChecking && <Spinner aria-hidden="true" />}
                {isChecking ? 'Checking…' : 'Go →'}
              </CTA>

              <Status role="status" aria-live="polite">
                {statusMsg}
              </Status>
            </form>
            </Card>
            </CardWrapper>
            </Page>
          )}
    
    </div>
  );
}